import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReauth } from '../../baseQuery';
import {
  Employed,
  EmployedQueryType,
  EmployedsQueryType,
  FiltersType,
  GetEmployedsResponseType,
  GetTabEmployedsResponseType,
  InviteBodyType,
  InviteEmployedsResponseType,
  InviteMultipleEmployedsResponseType,
  TabEmployedsQueryType,
  TabsTypes,
  GetСategoriesEmployedsType,
  CategoryEmployedsQueryType,
} from './type';

const getToken = () => localStorage.getItem('token');
const getRole = () => localStorage.getItem('role');

export const getAuthorizationHeader = () => {
  const token = getToken();
  return token ? `Bearer ${token}` : '';
};

export const employedsApi = createApi({
  reducerPath: 'employedsApi',
  tagTypes: [
    'Employeds',
    'Employed',
    'Tags',
    'EmployedsTabs',
    'Contracts',
    'EmployedsFilters',
    'EmployedsCategory',
  ],
  refetchOnReconnect: true,
  keepUnusedDataFor: 15,
  baseQuery: baseQueryWithReauth(),
  endpoints: (build) => ({
    getEmployeds: build.query<GetEmployedsResponseType, EmployedsQueryType>({
      query: ({
        page,
        per_page,
        filter,
        searchValue,
        sort,
        genders,
        issues,
        nationalities,
        limit_from,
        limit_to,
        date_add,
        contracts,
        categories,
      }) => {
        const searchParams = new URLSearchParams();

        searchParams.set('page', page.toString());
        searchParams.set('per_page', per_page?.toString() || '50');

        if (filter) {
          searchParams.set('filter', filter);
        }

        if (sort) {
          searchParams.set('sort', sort);
        }

        if (searchValue) {
          searchParams.set('search', searchValue);
        } else {
          searchParams.delete('search');
        }

        if (genders?.length) {
          genders.forEach((keyword) => searchParams.append('genders[]', keyword));
        }
        if (issues?.length) {
          issues.forEach((keyword) => searchParams.append('issues[]', keyword));
        }
        if (nationalities?.length) {
          nationalities.forEach((keyword) =>
            searchParams.append('nationalities[]', keyword),
          );
        }
        if (categories?.length) {
          categories.forEach((keyword) => searchParams.append('categories[]', keyword));
        }

        if (contracts?.length) {
          contracts.forEach((keyword) => searchParams.append('contracts[]', keyword));
        }

        if (limit_from != null) {
          searchParams.set('limit_from', String(limit_from));
        }
        if (limit_to != null) {
          searchParams.set('limit_to', String(limit_to));
        }

        if (date_add?.start) {
          searchParams.set('date_add[start]', date_add.start);
        }
        if (date_add?.end) {
          searchParams.set('date_add[end]', date_add.end);
        }

        return {
          url:
            getRole() === 'director'
              ? `workers?${searchParams.toString()}`
              : `workers-app?${searchParams.toString()}`,
          method: 'GET',
        };
      },
      providesTags: () => ['Employeds'],
      serializeQueryArgs: ({ endpointName }) => {
        return endpointName;
      },
      merge: (currentCache, newItems, { arg }) => {
        if (arg.page === 1) {
          return newItems;
        }

        if (currentCache && newItems.data) {
          return {
            ...newItems,
            data: [...(currentCache.data || []), ...newItems.data],
          };
        }
        return newItems;
      },
      forceRefetch({ currentArg, previousArg }) {
        return currentArg !== previousArg;
      },
    }),
    getCategoryEmployeds: build.query<
      GetСategoriesEmployedsType,
      CategoryEmployedsQueryType
    >({
      query: ({ page, search }) => {
        const searchParams = new URLSearchParams();
        searchParams.set('page', page.toString());
        searchParams.set('per_page', '20');
        const q = search?.trim();
        if (q) {
          searchParams.set('search', q);
        }
        return {
          url: `customer-categories?${searchParams.toString()}`,
          method: 'GET',
        };
      },
      providesTags: () => ['EmployedsCategory'],
      serializeQueryArgs: ({ endpointName, queryArgs }) => {
        const q = (queryArgs.search ?? '').trim();
        return `${endpointName}(${q})`;
      },
      merge: (currentCache, newItems, { arg }) => {
        if (arg.page === 1) {
          return newItems;
        }

        if (currentCache && newItems.data) {
          const combined = [...(currentCache.data || []), ...newItems.data];
          const seen = new Set<number>();
          const data = combined.filter((c) => {
            if (seen.has(c.id)) return false;
            seen.add(c.id);
            return true;
          });
          return {
            ...newItems,
            data,
          };
        }
        return newItems;
      },
      forceRefetch({ currentArg, previousArg }) {
        return currentArg !== previousArg;
      },
    }),
    createCategoryEmployeds: build.mutation<GetСategoriesEmployedsType, { name: string }>(
      {
        query: ({ name }) => ({
          url: `customer-categories`,
          body: { name },
          method: 'POST',
        }),
        invalidatesTags: ['EmployedsCategory'],
      },
    ),
    updateCategoryEmployeds: build.mutation<
      GetСategoriesEmployedsType,
      { id: string; name: string }
    >({
      query: ({ id, name }) => ({
        url: `customer-categories/${id}`,
        body: { name },
        method: 'PUT',
      }),
      invalidatesTags: ['EmployedsCategory'],
    }),
    assignCategoryToWorker: build.mutation<
      GetСategoriesEmployedsType,
      { worker_id: string; category_id: string[] }
    >({
      query: ({ worker_id, category_id }) => ({
        url: `workers/${worker_id}/categories`,
        body: { category_ids: category_id },
        method: 'PUT',
      }),
      invalidatesTags: ['Employed'],
      // invalidatesTags: ['Employeds', 'EmployedsCategory', 'Employed', 'Contracts'],
    }),

    getTabEmployedsCustomer: build.query<TabsTypes, void>({
      query: () => ({
        url: `workers/tabs`,
        method: 'GET',
      }),
      providesTags: () => ['EmployedsTabs'],
    }),
    getFiltersEmployeds: build.query<FiltersType, void>({
      query: () => ({
        url: `workers/filters`,
        method: 'GET',
      }),
      providesTags: () => ['EmployedsFilters'],
    }),
    getEmployed: build.query<Employed, EmployedQueryType>({
      query: ({ id }) => ({
        url: `/workers/${id}`,
        method: 'GET',
      }),
      providesTags: () => ['Employed', 'Contracts'],
      keepUnusedDataFor: 0,
    }),
    getTabEmployeds: build.query<GetTabEmployedsResponseType, TabEmployedsQueryType>({
      query: ({ id, title }) => ({
        url: `/workers/${id}/tabs/${title}`,
        method: 'GET',
      }),
      providesTags: () => ['Tags'],
    }),
    inviteEmployeds: build.mutation<InviteEmployedsResponseType, InviteBodyType>({
      query: ({ body }) => ({
        url: `/workers/invite`,
        method: 'POST',
        body,
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          Authorization: getAuthorizationHeader(),
        },
      }),
      invalidatesTags: (result, error) => (error ? [] : ['Employeds']),
    }),
    inviteMultipleEmployeds: build.mutation<
      InviteMultipleEmployedsResponseType,
      FormData
    >({
      query: (formData) => ({
        url: `/workers/upload`,
        method: 'POST',
        body: formData,
        formData: true,
        headers: {
          Accept: 'application/json',
          Authorization: getAuthorizationHeader(),
        },
      }),
      invalidatesTags: (result, error) => (error ? [] : ['Employeds']),
    }),
    blockWorker: build.mutation<
      { success: boolean; message: string },
      { workerId: number; comment: string }
    >({
      query: ({ workerId, comment }) => ({
        url: `/workers/block/${workerId}`,
        method: 'POST',
        body: { comment },
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          Authorization: getAuthorizationHeader(),
        },
      }),
      invalidatesTags: ['Employed', 'Employeds'],
    }),
    unblockWorker: build.mutation<
      { success: boolean; message: string },
      { workerId: number }
    >({
      query: ({ workerId }) => ({
        url: `/workers/unblock/${workerId}`,
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          Authorization: getAuthorizationHeader(),
        },
      }),
      invalidatesTags: ['Employed', 'Employeds'],
    }),
    blockCommentWorker: build.mutation<void, { workerId: number; comment: string }>({
      query: ({ workerId, comment }) => ({
        url: `/workers/block-comment/${workerId}`,
        method: 'POST',
        body: { comment },
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          Authorization: getAuthorizationHeader(),
        },
      }),
      invalidatesTags: ['Employed', 'Employeds'],
    }),
    updateWorkerLimit: build.mutation<
      {
        success: boolean;
        message: string;
        data?: { limit: number | null; monthly_payment: number };
      },
      { workerId: number; limit?: number }
    >({
      query: ({ workerId, limit }) => ({
        url: `/workers/limit/${workerId}`,
        method: 'POST',
        body: limit !== undefined ? { limit } : {},
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          Authorization: getAuthorizationHeader(),
        },
      }),
      invalidatesTags: ['Employed', 'Employeds'],
    }),
  }),
});

export const {
  useGetTabEmployedsQuery,
  useGetEmployedQuery,
  useGetEmployedsQuery,
  useGetTabEmployedsCustomerQuery,
  useGetCategoryEmployedsQuery,
  useInviteEmployedsMutation,
  useInviteMultipleEmployedsMutation,
  useCreateCategoryEmployedsMutation,
  useUpdateCategoryEmployedsMutation,
  useAssignCategoryToWorkerMutation,
  useBlockWorkerMutation,
  useGetFiltersEmployedsQuery,
  useUnblockWorkerMutation,
  useBlockCommentWorkerMutation,
  useUpdateWorkerLimitMutation,
} = employedsApi;
